import { useEffect, useState, useCallback } from "react";
import { db } from "./firebase";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import * as XLSX from "xlsx";

// ── Static field groups (DP + Nominee + Other) ─────────────────────────────
const STATIC_FIELD_GROUPS = [
  {
    group: "🗂️ DP Details",
    fields: [
      { key: "fullName",               label: "Full Name" },
      { key: "code",                   label: "Unique Client Code" },
      { key: "dpName",                 label: "DP Name" },
      { key: "depository",             label: "Depository (NSDL/CDSL)" },
      { key: "category",               label: "Category" },
      { key: "representativeAssessee", label: "Representative Assessee" },
      { key: "dpId",                   label: "DP ID" },
      { key: "boClientId",             label: "BO/Client ID" },
      { key: "pan",                    label: "PAN" },
      { key: "dob",                    label: "DOB/DOI" },
      { key: "nameAsPerPan",           label: "Name as per PAN" },
      { key: "mobile",                 label: "MO (Mobile)" },
      { key: "email",                  label: "Email" },
      { key: "jointWith",              label: "Joint With" },
      { key: "upiId",                  label: "UPI ID" },
      { key: "bank1",                  label: "Bank 1" },
      { key: "bank2",                  label: "Bank 2" },
      { key: "primaryBank",            label: "Primary Bank" },
      { key: "aadhaar",                label: "Aadhaar No" },
      { key: "dpNote",                 label: "Note (DP)" },
    ],
  },
  {
    group: "👥 Nominee Details",
    fields: [
      { key: "nom_nameAsPerPan",  label: "Nominee Name (as per PAN)" },
      { key: "nom_aadhaarNumber", label: "Nominee Aadhaar" },
      { key: "nom_relation",      label: "Relation" },
    ],
  },
  {
    group: "📝 Other Details",
    fields: [
      { key: "reference",    label: "Reference" },
      { key: "sourceForIpo", label: "Source for IPO" },
      { key: "note",         label: "Note" },
    ],
  },
];

// Sub-fields inside each bank account entry
const BANK_SUB_FIELDS = [
  { sub: "bankName",      label: "Name" },
  { sub: "branch",        label: "Branch" },
  { sub: "nameAsPerBank", label: "Name as per Bank" },
  { sub: "accNumber",     label: "Account No" },
  { sub: "accountType",   label: "Account Type" },
  { sub: "ifsc",          label: "IFSC" },
  { sub: "micr",          label: "MICR" },
  { sub: "customerId",    label: "Customer ID" },
  { sub: "netBankingId",  label: "Net Banking ID" },
  { sub: "mo",            label: "MO" },
  { sub: "email",         label: "Email" },
  { sub: "atmCardNo",     label: "ATM Card No" },
  { sub: "expiry",        label: "Expiry" },
  { sub: "cvv",           label: "CVV" },
  { sub: "note",          label: "Note" },
];

// ── Build dynamic field groups based on max banks in the dataset ───────────
function buildFieldGroups(maxBanks) {
  const bankGroups = Array.from({ length: maxBanks }, (_, i) => ({
    group: `🏦 Bank Details (Account #${i + 1})`,
    fields: BANK_SUB_FIELDS.map((sf) => ({
      key:   `bank${i}_${sf.sub}`,
      label: `Bank ${i + 1} ${sf.label}`,
    })),
  }));

  return [
    STATIC_FIELD_GROUPS[0],   // DP Details
    STATIC_FIELD_GROUPS[1],   // Nominee
    ...bankGroups,             // Bank 1, Bank 2, … (dynamic)
    STATIC_FIELD_GROUPS[2],   // Other Details
  ];
}

// ── Resolve a field key → value from a user record ────────────────────────
function resolveField(u, key) {
  if (key.startsWith("nom_")) {
    const sub = key.replace("nom_", "");
    return u.nomineeDetails?.[sub] ?? "-";
  }
  const bankMatch = key.match(/^bank(\d+)_(.+)$/);
  if (bankMatch) {
    const idx = parseInt(bankMatch[1], 10);
    const sub = bankMatch[2];
    return u.bankDetails?.[idx]?.[sub] ?? "";
  }
  return u[key] ?? "";
}

// ── Export Modal ────────────────────────────────────────────────────────────
function ExportModal({ onClose, onExport, total, fieldGroups }) {
  const ALL_KEYS = fieldGroups.flatMap((g) => g.fields.map((f) => f.key));
  const [selected, setSelected] = useState(new Set(ALL_KEYS));

  useEffect(() => {
    setSelected(new Set(ALL_KEYS));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldGroups.length]);

  const labelOf = (key) => {
    for (const g of fieldGroups) {
      const f = g.fields.find((f) => f.key === key);
      if (f) return f.label;
    }
    return key;
  };

  const toggle = (key) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });

  const toggleGroup = (fields) => {
    const keys  = fields.map((f) => f.key);
    const allOn = keys.every((k) => selected.has(k));
    setSelected((prev) => {
      const n = new Set(prev);
      keys.forEach((k) => (allOn ? n.delete(k) : n.add(k)));
      return n;
    });
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3 className="modal-title">Export to Excel</h3>
            <p className="modal-sub">
              {total} record{total !== 1 ? "s" : ""} will be exported (current search results)
            </p>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-actions">
          <button className="modal-action-btn" onClick={() => setSelected(new Set(ALL_KEYS))}>
            ✔ Select All
          </button>
          <button className="modal-action-btn outline" onClick={() => setSelected(new Set())}>
            ✕ Clear All
          </button>
          <span className="modal-count">
            {selected.size} field{selected.size !== 1 ? "s" : ""} selected
          </span>
        </div>

        <div className="modal-body">
          {fieldGroups.map((g) => {
            const groupKeys = g.fields.map((f) => f.key);
            const allOn  = groupKeys.every((k) => selected.has(k));
            const someOn = groupKeys.some((k) => selected.has(k));
            return (
              <div className="modal-group" key={g.group}>
                <div className="modal-group-header" onClick={() => toggleGroup(g.fields)}>
                  <label className="modal-group-label">
                    <input
                      type="checkbox"
                      className="modal-checkbox"
                      checked={allOn}
                      ref={(el) => { if (el) el.indeterminate = someOn && !allOn; }}
                      onChange={() => toggleGroup(g.fields)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {g.group}
                  </label>
                </div>
                <div className="modal-fields">
                  {g.fields.map((f) => (
                    <label
                      key={f.key}
                      className={`modal-field-item ${selected.has(f.key) ? "checked" : ""}`}
                    >
                      <input
                        type="checkbox"
                        className="modal-checkbox"
                        checked={selected.has(f.key)}
                        onChange={() => toggle(f.key)}
                      />
                      {f.label}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="modal-footer">
          <button className="modal-cancel" onClick={onClose}>Cancel</button>
          <button
            className="modal-export-btn"
            onClick={() => onExport([...selected], labelOf)}
            disabled={selected.size === 0}
          >
            ⬇ Export {selected.size > 0 ? `(${selected.size} fields)` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main UserList ───────────────────────────────────────────────────────────
export default function UserList({ user }) {
  const [users, setUsers]         = useState([]);
  const [allUsers, setAllUsers]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [expanded, setExpanded]   = useState(null);
  const [search, setSearch]       = useState("");
  const [showModal, setShowModal] = useState(false);

  const fetchLatest = useCallback(async () => {
    try {
      const ref  = collection(db, "admins", user.uid, "users");
      const q    = query(ref, orderBy("createdAt", "desc"), limit(10));
      const snap = await getDocs(q);
      setUsers(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user.uid]);

  const fetchAll = useCallback(async () => {
    try {
      const ref  = collection(db, "admins", user.uid, "users");
      const snap = await getDocs(ref);
      setAllUsers(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      console.error(err);
    }
  }, [user.uid]);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);
  useEffect(() => {
    if (search && allUsers.length === 0) fetchAll();
  }, [search, allUsers.length, fetchAll]);

  // ── Filtered list — this is EXACTLY what gets exported ───────────────────
  const sourceData    = search ? allUsers : users;
  const filteredUsers = sourceData.filter((u) => {
    const v = search.toLowerCase();
    return (
      u.fullName?.toLowerCase().includes(v)    ||
      u.code?.toLowerCase().includes(v)        ||
      u.boClientId?.toLowerCase().includes(v)  ||
      u.pan?.toLowerCase().includes(v)         ||
      u.bank1?.toLowerCase().includes(v)       ||
      u.bank2?.toLowerCase().includes(v)       ||
      u.primaryBank?.toLowerCase().includes(v) ||
      u.dpId?.toLowerCase().includes(v)
    );
  });

  // ── Max banks across ALL filtered records → drives dynamic columns ────────
  const maxBanks = Math.max(
    1,
    ...filteredUsers.map((u) => u.bankDetails?.length ?? 0)
  );

  // fieldGroups rebuilt from filtered results — modal shows correct bank groups
  const fieldGroups = buildFieldGroups(maxBanks);
  const ALL_KEYS    = fieldGroups.flatMap((g) => g.fields.map((f) => f.key));

  // ── Export handler ────────────────────────────────────────────────────────
  // - Only filteredUsers rows
  // - Only selected columns
  // - If a user has 1 bank and maxBanks=2, Bank 2 columns are empty strings
  const handleExport = (selectedKeys, labelOf) => {
    const data = filteredUsers.map((u) => {
      const row = {};
      ALL_KEYS.forEach((key) => {
        if (!selectedKeys.includes(key)) return;
        row[labelOf(key)] = resolveField(u, key);
      });
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(data);

    // Auto column widths for readability
    const headers = Object.keys(data[0] ?? {});
    ws["!cols"] = headers.map((header) => ({
      wch: Math.min(
        40,
        Math.max(
          header.length,
          ...data.map((r) => String(r[header] ?? "").length)
        ) + 2
      ),
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Clients");
    XLSX.writeFile(wb, `clients_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
    setShowModal(false);
  };

  const toggle = (id) => setExpanded((prev) => (prev === id ? null : id));
  const fmt    = (val) =>
    val && val !== "" ? val : <span style={{ color: "#9ca3af" }}>—</span>;

  if (loading)
    return (
      <div className="ul-empty">
        <div className="ul-spinner" />
        <p>Loading records…</p>
      </div>
    );

  if (users.length === 0)
    return (
      <div className="ul-empty">
        <div className="ul-empty-icon">📭</div>
        <h3>No records found</h3>
        <p>Add a client using the <strong>Add Data</strong> tab.</p>
      </div>
    );

  return (
    <>
      {showModal && (
        <ExportModal
          total={filteredUsers.length}
          fieldGroups={fieldGroups}
          onClose={() => setShowModal(false)}
          onExport={handleExport}
        />
      )}

      <div className="ul-wrap">
        <div className="ul-topbar">
          <div>
            <h2 className="ul-title">Client Records</h2>
            <p className="ul-sub">
              {filteredUsers.length} record{filteredUsers.length !== 1 ? "s" : ""} found
              {search && (
                <span style={{ color: "#6366f1", fontWeight: 600 }}> (filtered)</span>
              )}
            </p>
          </div>
          <button className="ul-export-btn" onClick={() => setShowModal(true)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export Excel
            {search && filteredUsers.length > 0 && (
              <span style={{
                marginLeft: 6, background: "#6366f1", color: "#fff",
                borderRadius: 10, fontSize: 11, padding: "1px 6px", fontWeight: 700,
              }}>
                {filteredUsers.length}
              </span>
            )}
          </button>
        </div>

        {/* Search bar */}
        <div className="ul-search-wrap">
          <svg className="ul-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="ul-search"
            placeholder="Search by Name / Unique Code / BO Client ID / PAN / Bank Name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="ul-search-clear" onClick={() => setSearch("")}>✕</button>
          )}
        </div>

        {/* Info banner when search is active */}
        {search && (
          <div style={{
            margin: "0 0 12px", padding: "8px 14px", background: "#f0f9ff",
            border: "1px solid #bae6fd", borderRadius: 8, fontSize: 13,
            color: "#0369a1", display: "flex", alignItems: "center", gap: 8,
          }}>
            <span>🔍</span>
            <span>
              Showing <strong>{filteredUsers.length}</strong> result{filteredUsers.length !== 1 ? "s" : ""} for
              "<strong>{search}</strong>". Export will include only these {filteredUsers.length} record{filteredUsers.length !== 1 ? "s" : ""}.
            </span>
          </div>
        )}

        <div className="ul-list">
          {filteredUsers.length === 0 ? (
            <div className="ul-empty" style={{ padding: "40px 0" }}>
              <div className="ul-empty-icon">🔍</div>
              <h3>No results found</h3>
              <p>Try a different search term.</p>
            </div>
          ) : (
            filteredUsers.map((u, i) => (
              <div className="ul-card" key={u.id} style={{ animationDelay: `${i * 40}ms` }}>
                <div className="ul-card-header" onClick={() => toggle(u.id)}>
                  <div className="ul-avatar">
                    {(u.fullName ? u.fullName[0] : "U").toUpperCase()}
                  </div>
                  <div className="ul-card-info">
                    <span className="ul-card-name">{u.fullName || "—"}</span>
                    <span className="ul-card-meta">
                      {u.code || "—"} &nbsp;·&nbsp; {u.dpId || "—"} &nbsp;·&nbsp;{" "}
                      {u.boClientId || "—"} &nbsp;·&nbsp; {u.pan || "—"}
                    </span>
                  </div>
                  <div className="ul-chip">{u.depository || u.category || "—"}</div>
                  {u.bankDetails?.length > 0 && (
                    <div style={{
                      fontSize: 11, fontWeight: 700, background: "#eff6ff",
                      color: "#2563eb", borderRadius: 10, padding: "2px 8px",
                      border: "1px solid #bfdbfe", whiteSpace: "nowrap",
                    }}>
                      🏦 {u.bankDetails.length} bank{u.bankDetails.length > 1 ? "s" : ""}
                    </div>
                  )}
                  <div className={`ul-chevron ${expanded === u.id ? "open" : ""}`}>▾</div>
                </div>

                {expanded === u.id && (
                  <div className="ul-detail">

                    {/* DP Details */}
                    <div className="ul-detail-section">
                      <div className="ul-detail-heading">🗂️ DP Details</div>
                      <div className="ul-detail-grid">
                        {[
                          ["Full Name",           u.fullName],
                          ["Unique Client Code",  u.code],
                          ["DP Name",             u.dpName],
                          ["Depository",          u.depository],
                          ["Category",            u.category],
                          ["Rep. Assessee",       u.representativeAssessee],
                          ["DP ID",               u.dpId],
                          ["BO / Client ID",      u.boClientId,  true],
                          ["PAN",                 u.pan,         true],
                          ["DOB / DOI",           u.dob],
                          ["Name as per PAN",     u.nameAsPerPan],
                          ["Mobile (MO)",         u.mobile],
                          ["Email",               u.email],
                          ["Joint With",          u.jointWith],
                          ["UPI ID",              u.upiId],
                          ["Bank 1",              u.bank1],
                          ["Bank 2",              u.bank2],
                          ["Primary Bank",        u.primaryBank],
                          ["Aadhaar No",          u.aadhaar,     true],
                          ["Note (DP)",           u.dpNote],
                        ].map(([label, val, mono]) => (
                          <div className="ul-detail-field" key={label}>
                            <span>{label}</span>
                            <strong className={mono ? "mono" : ""}>{fmt(val)}</strong>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Nominee — only show if any value filled */}
                    {u.nomineeDetails &&
                      (u.nomineeDetails.nameAsPerPan ||
                       u.nomineeDetails.aadhaarNumber ||
                       u.nomineeDetails.relation) && (
                      <div className="ul-detail-section">
                        <div className="ul-detail-heading">👥 Nominee Details</div>
                        <div className="ul-detail-grid">
                          <div className="ul-detail-field">
                            <span>Name as per PAN</span>
                            <strong>{fmt(u.nomineeDetails.nameAsPerPan)}</strong>
                          </div>
                          <div className="ul-detail-field">
                            <span>Aadhaar</span>
                            <strong className="mono">{fmt(u.nomineeDetails.aadhaarNumber)}</strong>
                          </div>
                          <div className="ul-detail-field">
                            <span>Relation</span>
                            <strong>{fmt(u.nomineeDetails.relation)}</strong>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Bank Details — all accounts dynamically */}
                    {u.bankDetails?.length > 0 && (
                      <div className="ul-detail-section">
                        <div className="ul-detail-heading">🏦 Bank Details</div>
                        {u.bankDetails.map((b, bi) => (
                          <div className="ul-bank-block" key={bi}>
                            <div className="ul-bank-badge">Account #{bi + 1}</div>
                            <div className="ul-detail-grid">
                              {[
                                ["Bank Name",        b.bankName],
                                ["Branch",           b.branch],
                                ["Name as per Bank", b.nameAsPerBank],
                                ["Account No",       b.accNumber,    true],
                                ["Account Type",     b.accountType],
                                ["IFSC",             b.ifsc,         true],
                                ["MICR",             b.micr,         true],
                                ["Customer ID",      b.customerId,   true],
                                ["Net Banking ID",   b.netBankingId],
                                ["MO (Bank)",        b.mo],
                                ["Email (Bank)",     b.email],
                                ["ATM Card No",      b.atmCardNo,    true],
                                ["Expiry",           b.expiry,       true],
                                ["CVV",              b.cvv,          true],
                                ["Note",             b.note],
                              ].map(([label, val, mono]) => (
                                <div className="ul-detail-field" key={label}>
                                  <span>{label}</span>
                                  <strong className={mono ? "mono" : ""}>{fmt(val)}</strong>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Other Details */}
                    {(u.reference || u.sourceForIpo || u.note) && (
                      <div className="ul-detail-section">
                        <div className="ul-detail-heading">📝 Other Details</div>
                        <div className="ul-detail-grid">
                          <div className="ul-detail-field">
                            <span>Reference</span>
                            <strong>{fmt(u.reference)}</strong>
                          </div>
                          <div className="ul-detail-field">
                            <span>Source for IPO</span>
                            <strong>{fmt(u.sourceForIpo)}</strong>
                          </div>
                          <div className="ul-detail-field">
                            <span>Note</span>
                            <strong>{fmt(u.note)}</strong>
                          </div>
                        </div>
                      </div>
                    )}

                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}